import type {
  TrackingCheckoutSchema,
  TrackingPaymentSchema,
  TrackingProductRecommendationSchema,
  TrackingProductsSchema,
} from '@/schemas/tracking.product'
import type { z } from 'zod'

declare global {
  interface Window {
    dataLayer: unknown[]
  }
}

const mapDataLayerItems = (
  products?: Array<z.infer<typeof TrackingProductsSchema>>,
) =>
  (products || []).map(
    (p: z.infer<typeof TrackingProductsSchema>, index: number) => ({
      item_id: p.ProdutoID,
      item_name: p.Produto,
      concurso: p.Instituicao,
      carreira: p.Carreira,
      discount: p.Desconto,
      index: index + 1,
      status: p.Edital,
      item_category: p.Tipo,
      cargo: p.Cargo,
      price: p.Valor,
      quantity: 1,
      formacao: p.Formacao,
    }),
  )

export const ga4 = {
  viewItem: (properties: z.infer<typeof TrackingProductsSchema>) => {
    if (!window.dataLayer) return
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        currency: 'BRL',
        value: properties.Valor,
        items: [
          {
            item_id: properties.ProdutoID,
            item_name: properties.Produto,
            concurso: properties.Instituicao,
            carreira: properties.Carreira,
            discount: properties.Desconto,
            index: 0,
            status: properties.Edital,
            item_category: properties.Tipo,
            cargo: properties.Cargo,
            price: properties.Valor,
            quantity: 1,
            formacao: properties.Formacao,
          },
        ],
      },
    })
    window.dataLayer.push({ items: null })
    window.dataLayer.push({
      event: 'rmkt_view_item',
      value: properties.Valor,
      items: [
        {
          id: properties.ProdutoID,
          google_business_vertical: 'education',
        },
      ],
    })
  },
  viewItemList: (properties: {
    type: string
    items: Array<z.infer<typeof TrackingProductsSchema>>
  }) => {
    if (!window.dataLayer) return
    const items = mapDataLayerItems(properties.items)
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'view_item_list',
      ecommerce: {
        item_list_id: properties.type,
        item_list_name: properties.type,
        items: items.map((item, index) => {
          return {
            ...item,
            index: index + 1,
          }
        }),
      },
    })
    window.dataLayer.push({ items: null })
    window.dataLayer.push({
      event: 'rmkt_view_item_list',
      value: items.reduce((a, b: { price: number }) => a + (b?.price || 0), 0),
      items: items.map((t: { item_id: string }) => ({
        id: t?.item_id,
        google_business_vertical: 'education',
      })),
    })
  },
  viewRecommendation: (
    properties: z.infer<typeof TrackingProductRecommendationSchema>,
  ) => {
    if (!window.dataLayer) return
    window.dataLayer.push({
      event: 'view_recommendation',
      institution: properties.Instituicao,
      type: properties.Tipo,
      product: properties.Produto,
      item_id: properties.ProdutoID,
      post: properties.Post,
    })
  },
  clickRecommendation: (
    properties: z.infer<typeof TrackingProductRecommendationSchema>,
  ) => {
    if (!window.dataLayer) return
    window.dataLayer.push({
      event: 'click_recommendation',
      institution: properties.Instituicao,
      type: properties.Tipo,
      product: properties.Produto,
      item_id: properties.ProdutoID,
      post: properties.Post,
    })
  },
  beginCheckout: (properties: z.infer<typeof TrackingCheckoutSchema>) => {
    if (!window.dataLayer) return
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'begin_checkout',
      ecommerce: {
        currency: 'BRL',
        value: properties.Total,
        coupon: properties.Cupom,
        items: mapDataLayerItems(properties.Produtos),
      },
    })
  },
  addShippingInfo: (properties: z.infer<typeof TrackingCheckoutSchema>) => {
    if (!window.dataLayer) return
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'add_shipping_info',
      ecommerce: {
        currency: 'BRL',
        value: properties.Total,
        coupon: properties.Cupom,
        items: mapDataLayerItems(properties.Produtos),
      },
    })
  },
  addPaymentInfo: (
    properties: z.infer<typeof TrackingCheckoutSchema>,
    payment: z.infer<typeof TrackingPaymentSchema>,
  ) => {
    if (!window.dataLayer) return
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'add_payment_info',
      ecommerce: {
        currency: 'BRL',
        value: properties.Total,
        coupon: properties.Cupom,
        payment_type: payment.Pagamento,
        items: mapDataLayerItems(properties.Produtos),
      },
    })
  },
  confirmation: (
    properties: z.infer<typeof TrackingCheckoutSchema>,
    order?: number,
  ) => {
    if (!window.dataLayer) return
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        currency: 'BRL',
        value: properties.Total,
        tax: 0,
        shipping: 0,
        coupon: properties.Cupom,
        transaction_id: order,
        items: mapDataLayerItems(properties.Produtos),
      },
    })
    window.dataLayer.push({ items: null })
    window.dataLayer.push({
      event: 'rmkt_purchase',
      value: properties.Total,
      items: properties.Produtos?.map((t: { ProdutoID: string }) => ({
        id: t.ProdutoID,
        google_business_vertical: 'education',
      })),
    })
  },
}

export default ga4
